import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {SnackService} from '../../services/snack/snack.service';
import {ScrollLoad} from './ScrollLoad';
import {ValidationService} from '../../services/validation/validation.service';
import {TranslateService} from '@ngx-translate/core';
import {FloatLabelType, MatFormField} from '@angular/material/form-field';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger
} from '@angular/material/autocomplete';
import {UtilService} from '../../services/util/util.service';
import {finalize} from 'rxjs/operators';
import {Observable, Subscription} from 'rxjs';
import {SortOrderEnum} from '../../generated/sales-terminal-client';
import {MatDialog} from '@angular/material/dialog';
import {PeriodPipe} from '../../pipes/period/period.pipe';
import {BookingSortBy, TransactionSortBy} from '../../generated/sales-terminal-client';
import {cloneDeep} from "lodash-es";

export declare interface ScrollLoadService<Type> {
  search(data: PageableSearch): Observable<PageableResult<Type>>;
  displayScrollLoadOption(option: Type): string;
  displayScrollLoadSelection(selection: Type): string | undefined;
  getScrollLoad?(pageSize?: number, idKey?: string): ScrollLoad<Type>;
}

export interface PageableResult<Type> {
  items?: Array<Type>;
  total?: number;
}
export interface PageableSearch {
  page?: number;
  pageSize?: number;
}
export interface SortableSearch extends PageableSearch {
  sortBy?: string | BookingSortBy | TransactionSortBy;
  sortOrder?: SortOrderEnum;
}

@Component({
  selector: 'app-scroll-load',
  templateUrl: './scroll-load.component.html',
  styleUrls: ['./scroll-load.component.scss']
})
export class ScrollLoadComponent<Type> implements OnInit, OnDestroy {
  @ViewChild('anchorElement') anchorElement: MatFormField;
  @ViewChild('scrollLoadComponent', {static: true}) scrollLoadComponent: TemplateRef<HTMLInputElement>;
  @ViewChild('selectInput') selectInput: ElementRef<HTMLInputElement>;
  @ViewChild('object') autoCompleteObject: MatAutocomplete;
  @ViewChild('selectInput', { read: MatAutocompleteTrigger, static: false }) autoComplete: MatAutocompleteTrigger;

  @Input() scrollLoad: ScrollLoad<Type>;
  @Input() label: string;
  @Input() placeholder: string;
  @Input() searchService: ScrollLoadService<Type>;
  @Input() displayOption: (value?: Type) => string;
  @Input() displaySelection: (value?: Type) => string | undefined;
  @Input() minInput = 0;
  @Input() customFilters: any;
  @Input() selectedObject: Type;
  @Input() selectedObjectId!: number | string;
  @Input() selectedObjects!: Type[];
  @Input() selectedObjectIds!: (string|number)[];
  @Input() multi: boolean;
  @Input() disabled: boolean;
  @Input() withSuffix: boolean;
  @Input() withPrefix: boolean;
  @Input() maxWidth: boolean;
  @Input() objectIdsToAvoid: (string|number)[] = [];
  @Input() objectIdsCanSelect: (string|number)[] = [];
  @Input() clearOnBlur: boolean;
  @Input() withHint: boolean;
  @Input() panelClass: string;
  @Input() customQuery: (query?: string) => any;
  @Input() small: boolean;
  @Input() autoCompletePosition: 'auto' | 'above' | 'below' = 'auto';
  @Input() required: boolean;

  @Output() selectedObjectChange: EventEmitter<Type> = new EventEmitter<Type>();
  @Output() selectedObjectIdChange: EventEmitter<number | string> = new EventEmitter<number | string>();
  @Output() selectedObjectsChange: EventEmitter<Type[]> = new EventEmitter<Type[]>();
  @Output() selectedObjectIdsChange: EventEmitter<(string|number)[]> = new EventEmitter<(string|number)[]>();
  @Output() clearEvent: EventEmitter<any> = new EventEmitter<any>();

  private _fieldValue: string;
  @Input()
  public set fieldValue(value: string) {
    this._fieldValue = value;
    this.fieldValueChange.emit(this._fieldValue);
  }
  public get fieldValue(): string {
    return this._fieldValue;
  }
  @Output() public fieldValueChange: EventEmitter<string> = new EventEmitter<string>();

  public floatLabelValue: FloatLabelType = 'auto';
  private searchDebounce: any;
  private searchDebounceTime: number = 300;
  private searchDebounceSubscription: Subscription;
  private virtualScrollHeight: number;
  private _subs: Subscription[] = [];

  constructor(public utilService: UtilService,
              private viewContainerRef: ViewContainerRef,
              private translateService: TranslateService,
              private snackService: SnackService, // Do not remove! Used by external service.
              private periodPipe: PeriodPipe, // Do not remove! Used by external service.
              private cdr: ChangeDetectorRef,
              private dialog: MatDialog) {}

  ngOnInit(): void {
    this.viewContainerRef.createEmbeddedView(this.scrollLoadComponent);
    if (!this.displayOption) {
      this.displayOption = this.searchService.displayScrollLoadOption;
    }
    if (!this.displaySelection) {
      this.displaySelection = this.searchService.displayScrollLoadSelection;
    }
    window.addEventListener('scroll', this.scrollEvent, true);
    this._subs.push(this.utilService.virtualKeyboardOpenedChange.subscribe((value) => {
      const height = !!value ? ' height: calc(100% - ' + value + 'px) !important;' : '';
      this.dialog.openDialogs.forEach((ref) => {
        document.getElementById(ref.id).setAttribute('style', 'transform: none;' + height)
      });
      if (!!value) {
        if (!this.virtualScrollHeight) {
          const inputElement = this.selectInput.nativeElement.getBoundingClientRect();
          const freeSpace = window.screen.height - value;
          const maxHeight = inputElement.height * 10;
          if ((inputElement.y + inputElement.height + 100) >= freeSpace) {
            this.autoCompletePosition = 'above';
            this.virtualScrollHeight = inputElement.y - 100;
          } else {
            this.autoCompletePosition = 'below';
            this.virtualScrollHeight = freeSpace - inputElement.y;
          }
          if (this.virtualScrollHeight > maxHeight) {
            this.virtualScrollHeight = maxHeight;
          }
        }
        this.updatePanel();
      } else {
        delete this.virtualScrollHeight;
      }
    }));
  }

  updatePanel() {
    if (this.virtualScrollHeight) {
      setTimeout(() => {
        const panel = this.autoCompleteObject.panel;
        if (panel) {
          panel.nativeElement.setAttribute('style', 'max-height: ' + this.virtualScrollHeight + 'px !important');
        }
      }, 100);
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.scrollEvent);
    this._subs.forEach(s => s.unsubscribe());
    this._subs = [];
  }

  search(keyboardEvent?: KeyboardEvent, focusEvent?: FocusEvent): void {
    this.clearDebounceTimeout();
    if (focusEvent && this.minInput > 0) { return; }
    this.scrollLoad.allLoaded = false;
    this.scrollLoad.currentPage = 1;
    if (keyboardEvent && ValidationService.isEnterOrArrowKeysPressed(keyboardEvent)) { return; }
    let target: any = keyboardEvent?.target;
    if (focusEvent) {
      target = focusEvent.target;
    }
    const value: string = target?.value.toString();
    this.fieldValue = value;
    this.scrollLoad.filteringValue = ValidationService.validateString(value, this.minInput) ? value : '';
    if (!this.scrollLoad.loading && ((this.scrollLoad.filteringValue?.length) || !this.minInput)) {
      this.scrollLoad.objects = [];
      this.load();
    } else {
      this.scrollLoad.objects = [];
      this.selectedObject = null;
      this.selectedObjectId = null;
      this.selectedObjectChange.emit(this.selectedObject);
      this.selectedObjectIdChange.emit(this.selectedObjectId);
    }
    this.cdr.markForCheck();
  }

  scrollLoadObjects(): void {
    if (this.scrollLoad.allLoaded || this.scrollLoad.loading) { return; }
    this.load();
  }

  private load(): void {
    this.scrollLoad.loading = true;
    if (this.scrollLoad.filteringValue || !this.minInput) {
      Object.keys(this.customFilters || {}).forEach(key => (this.scrollLoad.filterData as any)[key] = this.customFilters[key]);
      if (this.scrollLoad.filterKey) {
        (this.scrollLoad.filterData as any)[this.scrollLoad.filterKey] = this.scrollLoad.filteringValue;
      } else if (this.customQuery) {
        const queryFilter = this.customQuery(this.scrollLoad.filteringValue) || {};
        Object.keys(queryFilter).forEach(key => (this.scrollLoad.filterData as any)[key] = queryFilter[key]);
      }
      (this.scrollLoad.filterData as any)['page'] = this.scrollLoad.currentPage;

      this.searchDebounce = setTimeout(() => {
        this.searchDebounceSubscription = this.searchService.search(this.scrollLoad.filterData).pipe(
          finalize(() => {
            this.finishLoad();
          })
        ).subscribe(result => {
          this.scrollLoad.objects = [...this.scrollLoad.objects, ...this.filterSelected(result.items)];
          this.scrollLoad.allLoaded = this.scrollLoad.currentPage++ * this.scrollLoad.pageSize > result.total;
        });
      }, this.searchDebounceTime);
    } else {
      this.finishLoad();
    }
  }
  private finishLoad(): void {
    this.scrollLoad.hasResults = this.scrollLoad.objects.length > 0;
    this.scrollLoad.loading = false;
    this.cdr.markForCheck();
    this.updatePanel();
  }
  private clearDebounceTimeout() {
    if (this.searchDebounce) {
      if (this.searchDebounceSubscription) {
        this.searchDebounceSubscription.unsubscribe();
        delete this.searchDebounceSubscription;
      }
      clearTimeout(this.searchDebounce);
      this.finishLoad();
    }
  }

  scrollTo(target: MatFormField): void {
    if (target && this.utilService.isMobile) {
      if (this.utilService.virtualKeyboardSupported) {
        const sub = this.utilService.virtualKeyboardOpenedChange.subscribe(value => {
          if (value) {
            sub.unsubscribe();
            this.scrollToCallback(target);
          }
        });
      } else {
        this.scrollToCallback(target);
      }
    }
  }

  scrollToCallback(target: MatFormField): void {
    window.requestAnimationFrame(() => {
      target._elementRef.nativeElement.scrollIntoView({
        behavior: 'auto',
        block: 'center',
        inline: 'center'
      });
      this.autoComplete.updatePosition();
    });
  }

  scrollEvent = (): void => {
    if(this.autoComplete.panelOpen) {
      window.requestAnimationFrame(() => {
        this.autoComplete.updatePosition();
      });
    }
  }

  isOptionVisible(object: Type): boolean {
    if (this.selectedObjectId === (object as any)[this.scrollLoad.idKey]) {
      return true;
    }
    if (this.objectIdsCanSelect.length && !this.objectIdsCanSelect.includes((object as any)[this.scrollLoad.idKey])) {
      return false;
    }
    return !this.objectIdsToAvoid.includes((object as any)[this.scrollLoad.idKey]);
  }

  select(selectEvent: MatAutocompleteSelectedEvent): void {
    this.multi ? this.selectMulti(selectEvent) : this.selectSingle(selectEvent);
    this.scrollLoad.hasResults = false;
    this.cdr.markForCheck();
  }

  private selectSingle(selectEvent: MatAutocompleteSelectedEvent): void {
    if (selectEvent?.option?.value) {
      this.selectedObject = cloneDeep(selectEvent.option.value);
      this.selectedObjectId = selectEvent.option.value[this.scrollLoad.idKey] || null;
      this.selectedObjectChange.emit(this.selectedObject);
      this.selectedObjectIdChange.emit(this.selectedObjectId);
      this.cdr.markForCheck();
    }
  }

  get getPlaceholder(): string {
    return this.placeholder ? this.translateService.instant(this.placeholder) : null;
  }

  clear(): void {
    this.scrollLoad.reset();
    this.selectedObject = null;
    this.selectedObjectId = null;
    this.selectedObjectChange.emit(this.selectedObject);
    this.selectedObjectIdChange.emit(this.selectedObjectId);
    if (this.multi) {
      this.selectedObjects = [];
      this.selectedObjectIds = [];
      this.selectedObjectsChange.emit(this.selectedObjects);
      this.selectedObjectIdsChange.emit(this.selectedObjectIds);
    }
    this.clearEvent.emit();
    this.cdr.markForCheck();
  }

  clearIfEmpty(): void {
    if (this.clearOnBlur) {
      setTimeout(() => {
        if (this.multi ? !this.selectedObjectIds?.length : !this.selectedObjectId) {
          this.clear();
        }
      }, 100);
    }
  }

  // MULTISELECT
  private filterSelected(objects: Type[]): Type[] {
    return this.multi ?
      objects.filter((o: any) => (!this.selectedObjects?.length ||
        this.selectedObjects
          .map((i: any) => i[this.scrollLoad.idKey])
          .indexOf(o[this.scrollLoad.idKey]) === -1))
      : objects;
  }

  get hasSelectedObjects(): boolean {
    return this.selectedObjects?.length > 0;
  }

  private selectMulti(selectEvent: MatAutocompleteSelectedEvent): void {
    if (!this.selectedObjects) {
      this.selectedObjects = [];
      this.selectedObjectIds = [];
    }
    if (this.selectedObjects.findIndex((object: Type) => (object as any)[this.scrollLoad.idKey] === selectEvent.option.value[this.scrollLoad.idKey]) === -1) {
      this.selectedObjects.push(selectEvent.option.value);
      this.selectedObjectsChange.emit(this.selectedObjects);
      this.selectedObjectIds.push(selectEvent.option.value.id);
      this.selectedObjectIdsChange.emit(this.selectedObjectIds);
    }
    this.scrollLoad.object = null;
    if (this.multi) {
      this.selectInput.nativeElement.value = '';
      this.selectInput.nativeElement.blur();
    }
    this.scrollLoad.objects = [];
  }

  removeObject(item: Type): void {
    this.selectedObjects.splice(this.selectedObjects.indexOf(item), 1);
    this.selectedObjectIds.splice(this.selectedObjects.indexOf((item as any)[this.scrollLoad.idKey]), 1);
    this.cdr.markForCheck();
  }
}
